import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Check, X, Edit3 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface InlineEditableTextProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  textClassName?: string;
  editClassName?: string;
  disabled?: boolean;
  required?: boolean;
}

export function InlineEditableText({
  value,
  onSave,
  placeholder = "Click to edit...",
  multiline = false,
  className,
  textClassName,
  editClassName,
  disabled = false,
  required = false,
}: InlineEditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [originalValue, setOriginalValue] = useState(value);

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value);
    setOriginalValue(value);
  }, [value]);

  const handleSave = async () => {
    if (required && inputValue.trim() === "") {
      return;
    }

    if (inputValue !== originalValue) {
      setIsSaving(true);
      try {
        await onSave(inputValue);
        setOriginalValue(inputValue);
        setIsEditing(false);
      } catch {
        // Revert on error
        setInputValue(originalValue);
      } finally {
        setIsSaving(false);
      }
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setInputValue(originalValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Enter" && multiline && e.ctrlKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (disabled) {
    return (
      <div className={cn(textClassName, className)}>
        {value || <span className="text-muted-foreground italic">{placeholder}</span>}
      </div>
    );
  }

  if (isEditing) {
    const InputComponent = multiline ? Textarea : Input;

    return (
      <div className={cn("flex items-start gap-2", className)}>
        <InputComponent
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          variant="surface"
          className={cn("flex-1", editClassName)}
          autoFocus
          disabled={isSaving}
          {...(multiline && { rows: 3 })}
        />
        <div className="flex items-center gap-1 mt-1">
          <Button
            size="iconMd"
            variant="utility"
            onClick={handleSave}
            disabled={isSaving || (required && inputValue.trim() === "")}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            size="iconMd"
            variant="utility"
            onClick={handleCancel}
            disabled={isSaving}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group relative cursor-pointer rounded  py-1 hover:bg-accent/50 transition-colors",
        className,
      )}
      onClick={() => setIsEditing(true)}
    >
      <div className={cn("flex items-center gap-2", textClassName)}>
        {value || <span className="text-muted-foreground italic">{placeholder}</span>}
        <Edit3 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
      </div>
    </div>
  );
}
